import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { adminService } from '../../services/adminService';

interface Props {
  programId?: string;
}

const AttributionSettings: React.FC<Props> = ({ programId }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<{
    model: 'first_touch' | 'last_touch' | 'referrer';
    lookback_days: number;
    lifetime_enabled: boolean;
    recurring_enabled: boolean;
  }>({ model: 'last_touch', lookback_days: 30, lifetime_enabled: true, recurring_enabled: true });

  useEffect(() => {
    const load = async () => {
      if (!programId) return;
      setLoading(true);
      try {
        const data = await adminService.getAttributionSettings(programId);
        if (data) setForm((prev) => ({ ...prev, ...data }));
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load attribution settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [programId]);

  const onSave = async () => {
    if (!programId) {
      toast.error('Program not selected');
      return;
    }
    setLoading(true);
    try {
      await adminService.updateAttributionSettings(programId, form);
      toast.success('Attribution settings saved');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Attribution Settings</h2>
      {!programId && (
        <div className="p-4 bg-yellow-50 text-yellow-800 rounded mb-4">Create or select a program to configure settings.</div>
      )}
      <div className="grid gap-4 max-w-xl">
        <label className="block">
          <span className="text-sm text-gray-700">Attribution Model</span>
          <select
            className="mt-1 block w-full border rounded px-3 py-2"
            value={form.model}
            onChange={(e) => setForm({ ...form, model: e.target.value as any })}
            disabled={loading}
          >
            <option value="first_touch">First Touch</option>
            <option value="last_touch">Last Touch</option>
            <option value="referrer">Referrer</option>
          </select>
        </label>
        <label className="block">
          <span className="text-sm text-gray-700">Lookback Window (days)</span>
          <input
            type="number"
            className="mt-1 block w-full border rounded px-3 py-2"
            min={1}
            max={365}
            value={form.lookback_days}
            onChange={(e) => setForm({ ...form, lookback_days: Number(e.target.value) })}
            disabled={loading}
          />
        </label>
        <label className="inline-flex items-center space-x-2">
          <input
            type="checkbox"
            checked={form.lifetime_enabled}
            onChange={(e) => setForm({ ...form, lifetime_enabled: e.target.checked })}
            disabled={loading}
          />
          <span>Enable Lifetime Attribution</span>
        </label>
        <label className="inline-flex items-center space-x-2">
          <input
            type="checkbox"
            checked={form.recurring_enabled}
            onChange={(e) => setForm({ ...form, recurring_enabled: e.target.checked })}
            disabled={loading}
          />
          <span>Enable Recurring Commissions</span>
        </label>
      </div>
      <div className="mt-6">
        <button
          onClick={onSave}
          disabled={loading || !programId}
          className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default AttributionSettings;
